// src/pages/Vendors.tsx — §28.518.99.47.d
// Org-scoped Vendors list + create/edit modals. Backend contract in
// src/api/vendors.ts. Detail lives in src/pages/VendorDetail.tsx (opened
// as an in-page modal here — no separate route to keep nav simple).

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { SearchInput } from '../components/ui/SearchInput';
import { Pagination } from '../components/ui/Pagination';
import { CountBand } from '../components/ui/CountBand';
import { AuthContext } from '../context/AuthContext';
import { Role } from '../types';
import { isAdmin, isDealer, isCustomer, isPropertyManager } from '../utils/roles';
import { Briefcase, UserCheck, Users, Wrench, RefreshCw } from 'lucide-react';
import {
  listVendors,
  createVendor,
  createVendorEmployee,
  createVendorGrant,
  updateVendor,
  deleteVendor,
  Vendor,
} from '../api/vendors';
import { VendorDetail } from './VendorDetail';
import { apiList } from '../api/apiClient';

const PAGE_SIZE = 20;

const CATEGORIES = [
  'plumbing',
  'electrical',
  'cleaning',
  'landscaping',
  'security',
  'other',
];

type StatusFilter = 'all' | 'active' | 'revoked';

function getVendorStatus(v: Vendor): 'active' | 'revoked' {
  return v.revoked_at ? 'revoked' : 'active';
}

// Extract vendor scope from the JWT-derived user. Vendor employees see only
// their own vendor; vendor_admins see their vendor + employee management.
function useVendorSelf() {
  const { user } = useContext(AuthContext);
  const personType = (user as any)?.person_type as string | undefined;
  const vendorId = (user as any)?.vendor_id as string | undefined;
  const isVendor = personType === 'vendor' || personType === 'vendor_admin';
  const isVendorAdmin = personType === 'vendor_admin';
  return { user, personType, vendorId, isVendor, isVendorAdmin };
}

export const Vendors: React.FC = () => {
  const { user, isVendor, isVendorAdmin, vendorId } = useVendorSelf();
  const role = (user?.role || 'customer') as Role;

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);

  const [showCreate, setShowCreate] = useState(false);
  const [detailVendorId, setDetailVendorId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listVendors();
      // Vendor-side JWT: server should already scope, but defensive filter.
      const scoped = isVendor && vendorId
        ? rows.filter((v) => v.id === vendorId)
        : rows;
      setVendors(scoped);
    } catch (e: any) {
      const msg = String(e?.message || '');
      // A failed list is not an empty portfolio. Keep the failure visible so
      // managers do not mistake a network/auth outage for zero vendors.
      setVendors([]);
      setError(msg || 'Vendor list could not be loaded');
    } finally {
      setLoading(false);
    }
  }, [isVendor, vendorId]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-open the vendor detail for vendor / vendor_admin JWTs
  useEffect(() => {
    if (isVendor && vendorId && !detailVendorId) {
      setDetailVendorId(vendorId);
    }
  }, [isVendor, vendorId, detailVendorId]);

  const canCreate =
    isAdmin(role) || isDealer(role) || isCustomer(role) || isPropertyManager(role);
  const canDelete = isAdmin(role) || isDealer(role);

  const enriched = useMemo(() => {
    return vendors.map((v) => ({
      ...v,
      _status: getVendorStatus(v),
      _display_category: v.category
        ? v.category.charAt(0).toUpperCase() + v.category.slice(1)
        : '—',
    }));
  }, [vendors]);

  const activeCount = enriched.filter((v) => v._status === 'active').length;
  const revokedCount = enriched.filter((v) => v._status === 'revoked').length;

  const filtered = useMemo(() => {
    let result = enriched;
    if (statusFilter !== 'all') {
      result = result.filter((v) => v._status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          String(v.category || '').toLowerCase().includes(q) ||
          String(v.contact_email || '').toLowerCase().includes(q) ||
          String(v.contact_phone || '').includes(q)
      );
    }
    return result;
  }, [enriched, statusFilter, search]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, filtered.length);
  const pageData = filtered.slice(startIndex, endIndex);

  const handleCreated = (v: Vendor) => {
    setVendors((prev) => [v, ...prev]);
    setShowCreate(false);
    setDetailVendorId(v.id);
  };

  const handleDelete = async (v: Vendor) => {
    if (!canDelete) return;
    if (!window.confirm(`Revoke vendor "${v.name}"? Employees lose access immediately.`)) return;
    try {
      await deleteVendor(v.id);
      setVendors((prev) =>
        prev.map((row) =>
          row.id === v.id ? { ...row, revoked_at: new Date().toISOString() } : row
        )
      );
    } catch (e: any) {
      alert(`Failed to revoke vendor: ${e?.message || e}`);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Vendor',
      render: (v: any) => (
        <button
          onClick={() => setDetailVendorId(v.id)}
          className="font-medium text-brand-600 hover:text-brand-800 text-left"
        >
          {v.name}
        </button>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (v: any) => v._display_category,
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (v: any) => (
        <div className="text-sm">
          {v.contact_email && <div className="text-gray-700">{v.contact_email}</div>}
          {v.contact_phone && <div className="text-gray-500 text-xs">{v.contact_phone}</div>}
          {!v.contact_email && !v.contact_phone && <span className="text-gray-400">—</span>}
        </div>
      ),
    },
    {
      key: 'employees',
      header: 'Employees',
      render: (v: any) => v.employee_count ?? '—',
    },
    {
      key: 'grants',
      header: 'Property Grants',
      render: (v: any) => v.grant_count ?? (v.property_ids?.length ?? '—'),
    },
    {
      key: 'status',
      header: 'Status',
      render: (v: any) =>
        v._status === 'active' ? (
          <Badge status="active">Active</Badge>
        ) : (
          <Badge status="suspended">Revoked</Badge>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (v: any) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDetailVendorId(v.id)}
            className="row-open"
          >
            Open <span aria-hidden="true">→</span>
          </button>
          {canDelete && v._status === 'active' && (
            <button
              onClick={() => handleDelete(v)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100"
            >
              Revoke
            </button>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="shadow-list-page prototype-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-gray-500">
            Back-of-house service providers, contractors, and supply partners.{' '}
            <span className="text-gray-400 text-xs">
              Not listed in visitor directories or included in intercom simring.
            </span>
          </p>
        </div>
        {canCreate && !isVendor && (
          <Button onClick={() => setShowCreate(true)}>+ Add Vendor</Button>
        )}
      </div>

      <div className="prototype-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search vendors..." />
        <label>
          <small>Status</small>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="revoked">Revoked</option>
          </select>
        </label>
        <button className="prototype-refresh" onClick={load} aria-label="Refresh vendors">
          <RefreshCw /> <span>Refresh</span>
        </button>
        {canCreate && !isVendor && <Button onClick={() => setShowCreate(true)}>Add Vendor</Button>}
      </div>

      {!error && <CountBand value={filtered.length} detail="Vendors in the current accessible portfolio" />}

      {error && (
        <div role="alert" className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Summary cards */}
      {!error && <div className="legacy-summary-cards grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Vendors</p>
              <p className="text-2xl font-bold text-gray-900">{enriched.length}</p>
            </div>
            <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-brand-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-green-600">{activeCount}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Revoked</p>
              <p className="text-2xl font-bold text-gray-600">{revokedCount}</p>
            </div>
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <Wrench className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </Card>
      </div>}

      {/* Desktop table */}
      {!error && <Card padding={false} className="hidden md:block">
        <div className="legacy-table-controls p-4 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[240px]">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search vendors by name, category, contact..."
              />
            </div>
            <div className="flex items-center gap-2">
              {(['all', 'active', 'revoked'] as StatusFilter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setStatusFilter(f)}
                  className={`px-3 py-2 text-sm rounded-lg capitalize ${
                    statusFilter === f
                      ? 'bg-brand-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {f === 'all' ? 'All' : f}
                </button>
              ))}
            </div>
          </div>
        </div>

        <Table columns={columns} data={pageData} emptyMessage="No vendors yet" />

        <Pagination
          page={page}
          totalPages={totalPages}
          totalItems={filtered.length}
          startIndex={startIndex}
          endIndex={endIndex}
          onPageChange={setPage}
        />
      </Card>}

      {/* Mobile list */}
      {!error && <div className="md:hidden space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">
            No vendors found.
          </div>
        ) : (
          pageData.map((v: any) => (
            <div
              key={v.id}
              className="bg-white rounded-xl border border-gray-200 p-3"
              onClick={() => setDetailVendorId(v.id)}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{v.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {v._display_category} · {v.contact_email || v.contact_phone || '—'}
                  </p>
                </div>
                {v._status === 'active' ? (
                  <Badge status="active">Active</Badge>
                ) : (
                  <Badge status="suspended">Revoked</Badge>
                )}
              </div>
              <div className="mobile-card-actions">
                <button onClick={(e) => { e.stopPropagation(); setDetailVendorId(v.id); }} className="row-open">Open <span aria-hidden="true">→</span></button>
              </div>
            </div>
          ))
        )}
      </div>}

      {/* Create modal */}
      {showCreate && (
        <VendorCreateModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {/* Detail modal — reuses VendorDetail component. Vendor JWTs auto-open
          to their own vendor and can't switch away. */}
      {detailVendorId && (
        <Modal
          open={!!detailVendorId}
          onClose={() => !isVendor && setDetailVendorId(null)}
          title="Vendor"
          size="lg"
        >
          <VendorDetail
            vendorId={detailVendorId}
            onClose={() => !isVendor && setDetailVendorId(null)}
            onChanged={(v) =>
              setVendors((prev) => prev.map((row) => (row.id === v.id ? v : row)))
            }
            asAdmin={isVendorAdmin}
          />
        </Modal>
      )}
    </div>
  );
};

// ─── Create modal ─────────────────────────────────────────────────────────

interface VendorCreateModalProps {
  onClose: () => void;
  onCreated: (v: Vendor) => void;
  propertyId?: string; // if opened from PropertyDetail, auto-grant this property
}

export const VendorCreateModal: React.FC<VendorCreateModalProps> = ({
  onClose,
  onCreated,
  propertyId,
}) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [legalName, setLegalName] = useState('');
  const [website, setWebsite] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [primaryContact, setPrimaryContact] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [insuranceExpires, setInsuranceExpires] = useState('');
  const [notes, setNotes] = useState('');
  const blankEmployee = () => ({ first_name: '', last_name: '', job_title: '', email: '', phone: '', role: 'vendor' as 'vendor'|'vendor_admin' });
  const [employees, setEmployees] = useState([blankEmployee()]);
  const randomPin = () => String(Math.floor(100000 + Math.random() * 900000));
  const [properties, setProperties] = useState<any[]>([]);
  const [devices, setDevices] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<Record<string,{ device_ids:string[]; pin:string }>>(
    propertyId ? { [propertyId]: { device_ids: [], pin: randomPin() } } : {}
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([apiList('properties').catch(() => []), apiList('devices').catch(() => [])])
      .then(([propertyRows, deviceRows]) => { setProperties(propertyRows); setDevices(deviceRows); });
  }, []);

  const submit = async () => {
    if (!name.trim()) {
      setErr('Vendor name required');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const created = await createVendor({
        name: name.trim(),
        category: category || null,
        contact_email: contactEmail.trim() || null,
        contact_phone: contactPhone.trim() || null,
        legal_name: legalName.trim() || null,
        website: website.trim() || null,
        address_line1: address1.trim() || null,
        address_line2: address2.trim() || null,
        city: city.trim() || null,
        state: state.trim() || null,
        postal_code: postalCode.trim() || null,
        primary_contact_name: primaryContact.trim() || null,
        emergency_contact_name: emergencyName.trim() || null,
        emergency_contact_phone: emergencyPhone.trim() || null,
        license_number: licenseNumber.trim() || null,
        insurance_expires_at: insuranceExpires || null,
        notes: notes.trim() || null,
        metadata: propertyId ? { initial_property_id: propertyId } : {},
      });
      const enteredEmployees = employees.filter(e => e.first_name.trim() || e.last_name.trim() || e.email.trim() || e.phone.trim());
      for (const employee of enteredEmployees) {
        if (!employee.first_name.trim() || !employee.last_name.trim() || (!employee.email.trim() && !employee.phone.trim())) {
          throw new Error('Each employee needs a first name, last name, and email or phone.');
        }
        await createVendorEmployee(created.id, {
          ...employee,
          first_name: employee.first_name.trim(), last_name: employee.last_name.trim(),
          job_title: employee.job_title.trim() || undefined,
          email: employee.email.trim() || undefined, phone: employee.phone.trim() || undefined,
        });
      }
      for (const [assignedPropertyId, assignment] of Object.entries(assignments)) {
        await createVendorGrant(created.id, {
          property_id: assignedPropertyId,
          access_type: 'long_term',
          valid_from: new Date().toISOString(),
          valid_to: null,
          device_ids: assignment.device_ids,
          allowed_methods: ['pin', 'card', 'face', 'relay_button', 'mobile_app'],
          pin_code: assignment.pin,
        } as any);
      }
      onCreated({ ...created, employee_count: enteredEmployees.length, grant_count: Object.keys(assignments).length, property_ids: Object.keys(assignments) });
    } catch (e: any) {
      setErr(e?.message || 'Failed to create vendor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Add Vendor" size="xl">
      <div className="space-y-5 max-h-[75vh] overflow-y-auto pr-1">
        {err && (
          <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
            {err}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Vendor Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Plumbing"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Legal Business Name</label><input value={legalName} onChange={e=>setLegalName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="">— Select —</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Contact Email</label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Contact Phone</label>
            <input
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Primary Contact Name</label><input value={primaryContact} onChange={e=>setPrimaryContact(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Website</label><input type="url" value={website} onChange={e=>setWebsite(e.target.value)} placeholder="https://" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
        </div>
        <fieldset className="border border-gray-300 rounded-lg p-3 space-y-3">
          <legend className="px-1 text-xs font-bold uppercase tracking-wider text-gray-600">Business Address</legend>
          <input value={address1} onChange={e=>setAddress1(e.target.value)} placeholder="Street address" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          <input value={address2} onChange={e=>setAddress2(e.target.value)} placeholder="Suite, unit, or floor" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3"><input value={city} onChange={e=>setCity(e.target.value)} placeholder="City" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" /><input value={state} onChange={e=>setState(e.target.value)} placeholder="State" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" /><input value={postalCode} onChange={e=>setPostalCode(e.target.value)} placeholder="Postal code" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
        </fieldset>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-gray-600 mb-1">License / Certification Number</label><input value={licenseNumber} onChange={e=>setLicenseNumber(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Insurance Expiration</label><input type="date" value={insuranceExpires} onChange={e=>setInsuranceExpires(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Emergency Contact</label><input value={emergencyName} onChange={e=>setEmergencyName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Emergency Phone</label><input type="tel" value={emergencyPhone} onChange={e=>setEmergencyPhone(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
        </div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Vendor Notes</label><textarea value={notes} onChange={e=>setNotes(e.target.value)} className="w-full min-h-20 px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>

        <fieldset className="border border-gray-300 rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between"><legend className="text-xs font-bold uppercase tracking-wider text-gray-600">Employees</legend><Button variant="secondary" onClick={()=>setEmployees(v=>[...v,blankEmployee()])}>+ Add Employee</Button></div>
          <p className="text-xs text-gray-500">Add any number of vendor employees now. Employees can also be managed later from the vendor detail page.</p>
          {employees.map((employee,index)=><div key={index} className="border border-gray-200 bg-gray-50 p-3 space-y-2">
            <div className="flex justify-between"><span className="text-sm font-semibold text-gray-800">Employee {index+1}</span>{employees.length>1&&<button type="button" onClick={()=>setEmployees(v=>v.filter((_,i)=>i!==index))} className="text-xs text-red-600">Remove</button>}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input value={employee.first_name} onChange={e=>setEmployees(v=>v.map((x,i)=>i===index?{...x,first_name:e.target.value}:x))} placeholder="First name" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input value={employee.last_name} onChange={e=>setEmployees(v=>v.map((x,i)=>i===index?{...x,last_name:e.target.value}:x))} placeholder="Last name" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input value={employee.job_title} onChange={e=>setEmployees(v=>v.map((x,i)=>i===index?{...x,job_title:e.target.value}:x))} placeholder="Job title" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <select value={employee.role} onChange={e=>setEmployees(v=>v.map((x,i)=>i===index?{...x,role:e.target.value as any}:x))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"><option value="vendor">Vendor Employee</option><option value="vendor_admin">Vendor Admin</option></select>
              <input type="email" value={employee.email} onChange={e=>setEmployees(v=>v.map((x,i)=>i===index?{...x,email:e.target.value}:x))} placeholder="Email" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input type="tel" value={employee.phone} onChange={e=>setEmployees(v=>v.map((x,i)=>i===index?{...x,phone:e.target.value}:x))} placeholder="Phone" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>)}
        </fieldset>

        <fieldset className="border border-gray-300 rounded-lg p-3 space-y-3">
          <legend className="px-1 text-xs font-bold uppercase tracking-wider text-gray-600">Property & Device Assignments</legend>
          <p className="text-xs text-gray-500">Choose each property this vendor can access, the devices available there, and its generated vendor PIN.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {properties.map((property:any) => {
              const pid=String(property.uuid||property.id); const selected=!!assignments[pid];
              return <label key={pid} className={`flex items-start gap-2 border p-3 ${selected?'border-brand-500 bg-brand-50':'border-gray-200 bg-white'}`}>
                <input type="checkbox" checked={selected} disabled={!!propertyId&&pid!==propertyId} onChange={()=>setAssignments(current=>{const next={...current};if(selected)delete next[pid];else next[pid]={device_ids:[],pin:randomPin()};return next;})}/>
                <span><span className="block text-sm font-semibold text-gray-900">{property.name||'Unnamed property'}</span><span className="block text-xs text-gray-500">{[property.address,property.city,property.state].filter(Boolean).join(', ')}</span></span>
              </label>;
            })}
          </div>
          {Object.entries(assignments).map(([pid,assignment])=>{
            const property=properties.find((p:any)=>String(p.uuid||p.id)===pid);
            const eligible=devices.filter((d:any)=>String(d.property_id)===pid&&/^[0-9a-f-]{36}$/i.test(String(d.uuid||d.id)));
            return <div key={pid} className="border border-gray-200 bg-gray-50 p-3 space-y-3">
              <div className="flex items-center justify-between"><span className="text-sm font-semibold text-gray-900">{property?.name||'Selected property'}</span>{eligible.length>0&&<button type="button" className="text-xs text-brand-700" onClick={()=>setAssignments(v=>({...v,[pid]:{...v[pid],device_ids:v[pid].device_ids.length===eligible.length?[]:eligible.map((d:any)=>String(d.uuid||d.id))}}))}>{assignment.device_ids.length===eligible.length?'Clear devices':'Select all devices'}</button>}</div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Random Vendor PIN</label><div className="flex gap-2"><input readOnly value={assignment.pin} className="flex-1 px-3 py-2 border border-gray-300 bg-white font-mono font-bold tracking-widest text-sm"/><Button variant="secondary" onClick={()=>setAssignments(v=>({...v,[pid]:{...v[pid],pin:randomPin()}}))}>Regenerate</Button></div></div>
              {eligible.length===0?<p className="text-xs text-gray-500">No assignable devices found. Property access will still be saved.</p>:<div className="grid grid-cols-1 md:grid-cols-2 gap-2">{eligible.map((device:any)=>{const did=String(device.uuid||device.id),checked=assignment.device_ids.includes(did);return <label key={did} className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={checked} onChange={()=>setAssignments(v=>({...v,[pid]:{...v[pid],device_ids:checked?v[pid].device_ids.filter(x=>x!==did):[...v[pid].device_ids,did]}}))}/><span>{device.name||'Unnamed device'} <small className="text-gray-400">{device.device_type||device.type||''}</small></span></label>})}</div>}
            </div>;
          })}
        </fieldset>

        <div className="pt-2 flex items-center justify-end gap-2 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Cancel
          </button>
          <Button onClick={submit} disabled={saving}>
            {saving ? 'Creating…' : 'Create Vendor'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Re-export the update helper so PropertyDetail's inline VendorsTab can also
// tweak vendor names without cloning the modal.
export { updateVendor };
